import { useState, useCallback, useMemo, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@learn-me/ui";
import {
  Volume2,
  Loader2,
  Check,
  X,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Headphones,
  RefreshCw,
} from "lucide-react";
import { adminApi, type ChapterSection } from "../../api/adminApi";
import toast from "react-hot-toast";

interface BulkAudioPanelProps {
  chapterId: string;
  onAudioGenerated?: () => void;
}

/**
 * Panel for bulk audio generation for all sections in a chapter.
 * Shows current audio status and allows generating audio for all sections.
 */
export function BulkAudioPanel({
  chapterId,
  onAudioGenerated,
}: BulkAudioPanelProps) {
  const queryClient = useQueryClient();
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState("es-MX-DaliaNeural");
  const abortControllerRef = useRef<AbortController | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);

  // Fetch sections to know audio status
  const { data: sections = [], isLoading: sectionsLoading } = useQuery({
    queryKey: ["admin-chapter-sections", chapterId],
    queryFn: () => adminApi.getChapterSections(chapterId),
  });

  // Fetch available voices
  const { data: voices = [] } = useQuery({
    queryKey: ["tts-voices"],
    queryFn: () => adminApi.getTTSVoices(),
    staleTime: Infinity,
  });

  // Calculate audio stats
  const stats = useMemo(() => {
    const total = sections.length;
    const withAudio = sections.filter((s) => s.audioUrl).length;
    const withoutAudio = total - withAudio;
    const percentage = total > 0 ? Math.round((withAudio / total) * 100) : 0;
    return { total, withAudio, withoutAudio, percentage };
  }, [sections]);

  // Generate all audio mutation
  const generateMutation = useMutation({
    mutationFn: async () => {
      abortControllerRef.current = new AbortController();
      return adminApi.generateAllSectionAudios(chapterId, {
        voice: selectedVoice,
      });
    },
    onSuccess: (results) => {
      const successCount = results.length;
      const failedCount = stats.withoutAudio - successCount;

      if (failedCount > 0) {
        toast.success(
          `Audio generado para ${successCount} secciones. ${failedCount} fallaron.`,
        );
      } else {
        toast.success(`Audio generado para ${successCount} secciones`);
      }

      queryClient.invalidateQueries({
        queryKey: ["admin-chapter-sections", chapterId],
      });
      onAudioGenerated?.();
    },
    onError: (error) => {
      if (isCancelling) {
        toast.success("Generación cancelada");
        setIsCancelling(false);
      } else {
        toast.error(
          error instanceof Error
            ? error.message
            : "Error al generar audio masivo",
        );
      }
    },
  });

  const handleGenerate = useCallback(() => {
    if (stats.withoutAudio === 0) {
      toast.success("Todas las secciones ya tienen audio");
      return;
    }
    generateMutation.mutate();
  }, [generateMutation, stats.withoutAudio]);

  const handleCancel = useCallback(() => {
    if (abortControllerRef.current) {
      setIsCancelling(true);
      abortControllerRef.current.abort();
    }
  }, []);

  const isGenerating = generateMutation.isPending;

  // Sections list for detailed view
  const sectionsList = useMemo(
    () =>
      sections.map((s: ChapterSection) => ({
        id: s.id,
        title: s.title,
        hasAudio: !!s.audioUrl,
        duration: s.audioDuration,
        voice: s.audioVoice,
      })),
    [sections],
  );

  if (sectionsLoading) {
    return (
      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
        <div className="flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin text-indigo-600" />
          <span className="text-sm text-indigo-700">Cargando secciones...</span>
        </div>
      </div>
    );
  }

  if (sections.length === 0) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="flex items-center gap-2 text-gray-500">
          <Headphones className="h-4 w-4" />
          <span className="text-sm">No hay secciones para generar audio</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-indigo-50 border border-indigo-200 rounded-lg overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 bg-indigo-100">
        <div className="flex items-center gap-2">
          <Headphones className="h-4 w-4 text-indigo-600" />
          <span className="text-sm font-medium text-indigo-800">
            Audio Masivo
          </span>
        </div>
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="p-1 rounded hover:bg-indigo-200"
        >
          {isExpanded ? (
            <ChevronUp className="h-4 w-4 text-indigo-600" />
          ) : (
            <ChevronDown className="h-4 w-4 text-indigo-600" />
          )}
        </button>
      </div>

      <div className="p-3 space-y-3">
        {/* Stats */}
        <div className="flex items-center justify-between">
          <div className="text-sm text-indigo-700">
            <span className="font-medium">{stats.withAudio}</span> de{" "}
            <span className="font-medium">{stats.total}</span> secciones con
            audio
          </div>
          <div
            className={`text-xs font-medium px-2 py-0.5 rounded ${
              stats.percentage === 100
                ? "bg-green-100 text-green-700"
                : stats.percentage > 50
                  ? "bg-yellow-100 text-yellow-700"
                  : "bg-red-100 text-red-700"
            }`}
          >
            {stats.percentage}%
          </div>
        </div>

        {/* Progress bar */}
        <div className="w-full h-2 bg-indigo-200 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-300 ${
              stats.percentage === 100 ? "bg-green-500" : "bg-indigo-500"
            }`}
            style={{ width: `${stats.percentage}%` }}
          />
        </div>

        {/* Voice selector */}
        <div className="flex items-center gap-2">
          <select
            value={selectedVoice}
            onChange={(e) => setSelectedVoice(e.target.value)}
            className="flex-1 p-2 border rounded text-xs bg-white"
            disabled={isGenerating}
          >
            {voices.map((voice) => (
              <option key={voice.id} value={voice.id}>
                {voice.name}
              </option>
            ))}
          </select>
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          {isGenerating ? (
            <>
              <Button type="button" size="sm" className="flex-1" disabled>
                <Loader2 className="h-3 w-3 animate-spin mr-2" />
                Generando...
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleCancel}
                className="text-red-600 border-red-200 hover:bg-red-50"
              >
                <X className="h-3 w-3" />
              </Button>
            </>
          ) : stats.withoutAudio > 0 ? (
            <Button
              type="button"
              className="w-full"
              size="sm"
              onClick={handleGenerate}
            >
              <Volume2 className="h-3 w-3 mr-2" />
              Generar Audio ({stats.withoutAudio} pendientes)
            </Button>
          ) : (
            <Button
              type="button"
              variant="outline"
              className="w-full"
              size="sm"
              onClick={handleGenerate}
            >
              <RefreshCw className="h-3 w-3 mr-2" />
              Regenerar Todos
            </Button>
          )}
        </div>

        {/* Expanded section list */}
        {isExpanded && (
          <div className="mt-3 border-t border-indigo-200 pt-3">
            <p className="text-xs font-medium text-indigo-700 mb-2">
              Estado por sección:
            </p>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {sectionsList.map((section, index) => (
                <div
                  key={section.id}
                  className="flex items-center justify-between text-xs py-1 px-2 bg-white rounded"
                >
                  <span className="truncate flex-1 text-gray-700">
                    {index + 1}. {section.title}
                  </span>
                  {section.hasAudio ? (
                    <span className="flex items-center gap-1 text-green-600 ml-2">
                      <Check className="h-3 w-3" />
                      {section.duration && (
                        <span className="text-gray-500">
                          {formatDuration(section.duration)}
                        </span>
                      )}
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-gray-400 ml-2">
                      <AlertCircle className="h-3 w-3" />
                      Pendiente
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Generation info */}
        {isGenerating && (
          <div className="text-xs text-indigo-600 bg-indigo-100 rounded p-2">
            <p className="font-medium">Generando audio...</p>
            <p className="text-indigo-500 mt-1">
              Este proceso puede tardar varios minutos dependiendo del número de
              secciones. Puedes continuar trabajando mientras se genera.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}
